import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, shareReplay, map, catchError, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Most API responses for configuration return an array of OrgConfList objects
 * (see backend orgconf.xsd).  Only a few fields are required here,
 * the only one that the front-end currently cares about is **delice**.
 */
export interface OrgConfList {
  id?: number;
  delice?: string; // comes back as "0" or "1" (sometimes as string)
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class OrgconfService {
  // baseUrl should point to the Spring backend root (including any "/api" prefix)
  // Environment files must supply a value without a trailing slash for consistency.
  private baseUrl = environment.apiUrl;

  /**
   * Ensure we don't end up with double slashes when joining paths.
   */
  private sanitizeUrl(url: string): string {
    return url.replace(/\/+$/g, '');
  }

  // subject holding current configuration; new values pushed by refreshConfig
  private configSubject = new BehaviorSubject<OrgConfList[]>([]);

  constructor(
    private http: HttpClient,
    private auth: AuthService
  ) {}

  /**
   * low-level loader which performs the HTTP call and caches result in subject
   */
private loadConfig(): Observable<OrgConfList[]> {
  let nameOrg = this.auth.getUserData()?.['nameOrg'] || '';

  if (!nameOrg) {
    const ud = this.auth.getUserData() as any;
    if (ud?.listStudent?.length) {
      nameOrg = ud.listStudent[0].nameOrg || '';
    }
  }

  const url = `${this.sanitizeUrl(this.baseUrl)}/config/pageOrgConf/${encodeURIComponent(nameOrg)}`;

  return this.http.get<OrgConfList[]>(url).pipe(
    tap((cfg) => this.configSubject.next(cfg)),
    catchError(err => {
      console.error('Erreur API pageOrgConf:', err);
      this.configSubject.next([]);
      return of([]);
    })
  );
}

  /**
   * Force a fresh fetch from backend. Components can call this when they
   * need to re-evaluate the configuration (e.g. on navigation).
   */
  refreshConfig(): Observable<OrgConfList[]> {
    return this.loadConfig();
  }

  /**
   * Observable stream of config array; updates whenever refreshConfig pushes
   * a new value.  Components should subscribe to this rather than calling
   * fetchConfig directly.
   */
  getConfig(): Observable<OrgConfList[]> {
    return this.configSubject.asObservable();
  }

  /**
   * Boolean stream derived from configuration; true when delice flag is set.
   */
  isDeliceEnabled(): Observable<boolean> {
    return this.configSubject.asObservable().pipe(
      map((arr: OrgConfList[]) => {
        if (!arr || arr.length === 0) {
          return false;
        }
        const val = arr[0]?.delice;
        return val === '1' || val === 'true' || Number(val) > 0;
      }),
      catchError(() => of(false))
    );
  }
}
